import Color from 'colorjs.io';
import { radixReferenceColors } from './radixReferenceColors';
const SCALE_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const GRAY_SCALE_NAMES = [
    'gray',
    'mauve',
    'slate',
    'sage',
    'olive',
    'sand',
];
const ACCENT_SCALE_NAMES = [
    ...GRAY_SCALE_NAMES,
    'tomato',
    'red',
    'ruby',
    'crimson',
    'pink',
    'plum',
    'purple',
    'violet',
    'iris',
    'indigo',
    'blue',
    'cyan',
    'teal',
    'jade',
    'green',
    'grass',
    'brown',
    'orange',
    'sky',
    'mint',
    'lime',
    'yellow',
    'amber',
];
const LIGHTNESS_BEZIER_DARK = [1, 0, 1, 0];
const LIGHTNESS_BEZIER_LIGHT = [0, 2, 0, 2];
const lightAccentScales = Object.fromEntries(ACCENT_SCALE_NAMES.map((name) => [
    name,
    Object.values(radixReferenceColors[`${name}P3`]).map((value) => new Color(value).to('oklch')),
]));
const darkAccentScales = Object.fromEntries(ACCENT_SCALE_NAMES.map((name) => [
    name,
    Object.values(radixReferenceColors[`${name}DarkP3`]).map((value) => new Color(value).to('oklch')),
]));
const lightGrayScales = Object.fromEntries(GRAY_SCALE_NAMES.map((name) => [
    name,
    Object.values(radixReferenceColors[`${name}P3`]).map((value) => new Color(value).to('oklch')),
]));
const darkGrayScales = Object.fromEntries(GRAY_SCALE_NAMES.map((name) => [
    name,
    Object.values(radixReferenceColors[`${name}DarkP3`]).map((value) => new Color(value).to('oklch')),
]));
function oklchCoords(color) {
    const [lightness, chroma, hue] = color.coords;
    return [lightness ?? 0, chroma ?? 0, hue ?? 0];
}
function srgbCoords(color) {
    const [r, g, b] = color.to('srgb').coords;
    return [r ?? 0, g ?? 0, b ?? 0];
}
function requireColor(scale, index) {
    const color = scale[index];
    if (color === undefined) {
        throw new Error(`Missing color at scale index ${index}`);
    }
    return color;
}
function createBezierEasing(x1, y1, x2, y2) {
    const useFloat32 = typeof Float32Array === 'function';
    function sampleCurveX(t) {
        return (((1 - 3 * x2 + 3 * x1) * t + (3 * x2 - 6 * x1)) * t + 3 * x1) * t;
    }
    function sampleCurveY(t) {
        return (((1 - 3 * y2 + 3 * y1) * t + (3 * y2 - 6 * y1)) * t + 3 * y1) * t;
    }
    function sampleCurveDerivativeX(t) {
        return (3 * (1 - 3 * x2 + 3 * x1) * t + 2 * (3 * x2 - 6 * x1)) * t + 3 * x1;
    }
    if (!(x1 >= 0 && x1 <= 1 && x2 >= 0 && x2 <= 1)) {
        throw new Error('bezier x values must be in [0, 1] range');
    }
    if (x1 === y1 && x2 === y2) {
        return (value) => value;
    }
    const sampleValues = useFloat32
        ? new Float32Array(11)
        : Array.from({ length: 11 }, () => 0);
    for (let i = 0; i < 11; ++i) {
        sampleValues[i] = sampleCurveX(0.1 * i);
    }
    return function bezier(value) {
        if (value === 0) {
            return 0;
        }
        if (value === 1) {
            return 1;
        }
        function getTForX(x) {
            let intervalStart = 0;
            let currentSample = 1;
            for (; currentSample !== 10 && (sampleValues[currentSample] ?? 0) <= x; ++currentSample) {
                intervalStart += 0.1;
            }
            currentSample -= 1;
            const currentValue = sampleValues[currentSample] ?? 0;
            const nextValue = sampleValues[currentSample + 1] ?? 0;
            const dist = (x - currentValue) / (nextValue - currentValue);
            let guessForT = intervalStart + dist * 0.1;
            const initialSlope = sampleCurveDerivativeX(guessForT);
            if (initialSlope >= 0.001) {
                for (let i = 0; i < 4; ++i) {
                    const currentSlope = sampleCurveDerivativeX(guessForT);
                    if (currentSlope === 0) {
                        break;
                    }
                    const currentX = sampleCurveX(guessForT) - x;
                    guessForT -= currentX / currentSlope;
                }
                return guessForT;
            }
            if (initialSlope === 0) {
                return guessForT;
            }
            let bottom = intervalStart;
            let top = intervalStart + 0.1;
            let t = guessForT;
            for (let i = 0; Math.abs(sampleCurveX(t) - x) > 1e-7 && ++i < 10;) {
                if (sampleCurveX(t) - x > 0) {
                    top = t;
                }
                else {
                    bottom = t;
                }
                t = bottom + (top - bottom) / 2;
            }
            return t;
        }
        return sampleCurveY(getTForX(value));
    };
}
function mapLightness(target, values, bezierControlPoints) {
    const easing = createBezierEasing(bezierControlPoints[0] ?? 0, bezierControlPoints[1] ?? 0, bezierControlPoints[2] ?? 0, bezierControlPoints[3] ?? 0);
    return values.map((value, index, array) => {
        const lastIndex = array.length - 1;
        const firstValue = array[0] ?? 0;
        return value - (firstValue - target) * easing(1 - index / lastIndex);
    });
}
function getContrastColor(color) {
    const white = new Color('oklch', [1, 0, 0]);
    if (Math.abs(white.contrastAPCA(color)) < 40) {
        const [, chroma, hue] = oklchCoords(color);
        return new Color('oklch', [0.25, Math.max(0.08 * chroma, 0.04), hue]);
    }
    return white;
}
function tuneStep9(color, candidateScales) {
    const [lightness, chroma, hue] = oklchCoords(color);
    const adjustedChroma = lightness > 0.4 && !Number.isNaN(hue) ? 0.93 * chroma : chroma;
    const adjustedLightness = lightness > 0.4
        ? lightness - 0.03 / (lightness + 0.1)
        : lightness + 0.03 / (lightness + 0.1);
    const tuned = new Color('oklch', [adjustedLightness, adjustedChroma, hue]);
    let closest = tuned;
    let closestDistance = Infinity;
    for (const scale of candidateScales) {
        for (const candidate of scale) {
            const distance = tuned.deltaEOK(candidate);
            if (distance < closestDistance) {
                closestDistance = distance;
                closest = candidate;
            }
        }
    }
    tuned.coords[1] = closest.coords[1];
    tuned.coords[2] = closest.coords[2];
    return tuned;
}
function lerpRound(channel, ratio, backgroundChannel, round = true) {
    return round
        ? Math.round(backgroundChannel * (1 - ratio)) + Math.round(channel * ratio)
        : backgroundChannel * (1 - ratio) + channel * ratio;
}
function adjustChannel(channel, foreground, background, rounded, blendTowardBackground) {
    const withinBounds = blendTowardBackground
        ? foreground <= background
        : foreground >= background;
    if (withinBounds && foreground !== rounded) {
        return foreground > rounded ? channel + 1 : channel - 1;
    }
    return channel;
}
function getAlphaBlendParams(foreground, background, maxChannelValue, precision, fixedAlpha) {
    // blendTarget is the channel value the foreground blends toward:
    // maxChannelValue means "toward white", 0 means "toward black".
    let blendTarget = 0;
    const [fr, fg, fb] = foreground.map((value) => Math.round(value * maxChannelValue));
    const [br, bg, bb] = background.map((value) => Math.round(value * maxChannelValue));
    if (fr === undefined ||
        fg === undefined ||
        fb === undefined ||
        br === undefined ||
        bg === undefined ||
        bb === undefined) {
        throw new Error('Color is undefined');
    }
    if (fr > br || fg > bg || fb > bb) {
        blendTarget = maxChannelValue;
    }
    const redRatio = (fr - br) / (blendTarget - br);
    const greenRatio = (fg - bg) / (blendTarget - bg);
    const blueRatio = (fb - bb) / (blendTarget - bb);
    const allEqual = [redRatio, greenRatio, blueRatio].every((value) => value === redRatio);
    if (!fixedAlpha && allEqual) {
        const normalizedTarget = blendTarget / maxChannelValue;
        return [
            normalizedTarget,
            normalizedTarget,
            normalizedTarget,
            redRatio,
        ];
    }
    const clamp = (value) => Number.isNaN(value) ? 0 : Math.min(maxChannelValue, Math.max(0, value));
    const blendRatio = (Number.isNaN((fixedAlpha ?? Math.max(redRatio, greenRatio, blueRatio)) * precision)
        ? 0
        : Math.min(precision, Math.max(0, Math.ceil((fixedAlpha ?? Math.max(redRatio, greenRatio, blueRatio)) *
            precision)))) / precision;
    let redChannel = clamp(-((br * (1 - blendRatio) - fr) / blendRatio));
    let greenChannel = clamp(-((bg * (1 - blendRatio) - fg) / blendRatio));
    let blueChannel = clamp(-((bb * (1 - blendRatio) - fb) / blendRatio));
    redChannel = Math.ceil(redChannel);
    greenChannel = Math.ceil(greenChannel);
    blueChannel = Math.ceil(blueChannel);
    const roundedRed = lerpRound(redChannel, blendRatio, br);
    const roundedGreen = lerpRound(greenChannel, blendRatio, bg);
    const roundedBlue = lerpRound(blueChannel, blendRatio, bb);
    if (blendTarget === 0 || blendTarget === maxChannelValue) {
        const blendTowardBackground = blendTarget === 0;
        redChannel = adjustChannel(redChannel, fr, br, roundedRed, blendTowardBackground);
        greenChannel = adjustChannel(greenChannel, fg, bg, roundedGreen, blendTowardBackground);
        blueChannel = adjustChannel(blueChannel, fb, bb, roundedBlue, blendTowardBackground);
    }
    return [
        redChannel / maxChannelValue,
        greenChannel / maxChannelValue,
        blueChannel / maxChannelValue,
        blendRatio,
    ];
}
function normalizeHexAlpha(hex) {
    if (!hex.startsWith('#')) {
        return hex;
    }
    if (hex.length === 4) {
        const [, r, g, b] = hex;
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (hex.length === 5) {
        const [, r, g, b, a] = hex;
        return `#${r}${r}${g}${g}${b}${b}${a}${a}`;
    }
    return hex;
}
function toAlphaHex(foreground, background, alpha) {
    const [r, g, b, a] = getAlphaBlendParams(srgbCoords(new Color(foreground)), srgbCoords(new Color(background)), 255, 255, alpha);
    return normalizeHexAlpha(new Color('srgb', [r, g, b], a).toString({ format: 'hex' }));
}
function interpolateScale(target, referenceScales, background) {
    const distances = [];
    for (const [scaleName, colors] of Object.entries(referenceScales)) {
        for (const color of colors) {
            distances.push({
                scale: scaleName,
                distance: target.deltaEOK(color),
                color,
            });
        }
    }
    distances.sort((a, b) => a.distance - b.distance);
    const closestScales = distances.filter((entry, index, array) => index === array.findIndex((item) => item.scale === entry.scale));
    const isGrayScale = (entry) => entry !== undefined &&
        GRAY_SCALE_NAMES.includes(entry.scale);
    if (!closestScales.every(isGrayScale) && isGrayScale(closestScales[0])) {
        while (isGrayScale(closestScales[1])) {
            closestScales.splice(1, 1);
        }
    }
    const first = closestScales[0];
    const second = closestScales[1];
    if (first === undefined || second === undefined) {
        throw new Error('Not enough reference scales to interpolate');
    }
    const distanceBetweenScales = second.distance;
    const distanceToFirst = first.distance;
    const distanceBetweenRepresentatives = first.color.deltaEOK(second.color);
    const cosineA = (distanceToFirst ** 2 +
        distanceBetweenRepresentatives ** 2 -
        distanceBetweenScales ** 2) /
        (2 * distanceToFirst * distanceBetweenRepresentatives);
    const sineA = Math.sin(Math.acos(cosineA));
    const cosineB = (distanceBetweenScales ** 2 +
        distanceBetweenRepresentatives ** 2 -
        distanceToFirst ** 2) /
        (2 * distanceBetweenScales * distanceBetweenRepresentatives);
    const sineB = Math.sin(Math.acos(cosineB));
    const mixRatio = 0.5 * Math.max(0, cosineA / sineA / (cosineB / sineB));
    const firstScale = referenceScales[first.scale];
    const secondScale = referenceScales[second.scale];
    if (firstScale === undefined || secondScale === undefined) {
        throw new Error('Reference scale not found');
    }
    const mixedScale = SCALE_INDICES.map((index) => {
        const firstColor = firstScale[index];
        const secondColor = secondScale[index];
        if (firstColor === undefined || secondColor === undefined) {
            throw new Error('Reference color not found');
        }
        return new Color(Color.mix(firstColor, secondColor, mixRatio)).to('oklch');
    });
    const closestMixedColor = [...mixedScale].sort((a, b) => target.deltaEOK(a) - target.deltaEOK(b))[0];
    if (closestMixedColor === undefined) {
        throw new Error('Mixed scale is empty');
    }
    const [, targetChroma] = oklchCoords(target);
    const [, closestChroma] = oklchCoords(closestMixedColor);
    const chromaScale = closestChroma > 0 ? targetChroma / closestChroma : 0;
    for (const color of mixedScale) {
        const [, chroma] = oklchCoords(color);
        color.coords[1] = Math.min(1.5 * targetChroma, chroma * chromaScale);
        color.coords[2] = oklchCoords(target)[2];
    }
    const firstMixed = mixedScale[0];
    if (firstMixed === undefined) {
        throw new Error('Mixed scale is empty');
    }
    const [firstLightnessValue] = oklchCoords(firstMixed);
    if (firstLightnessValue > 0.5) {
        const lightnessValues = mixedScale.map((color) => oklchCoords(color)[0]);
        const mapped = mapLightness(Math.max(0, Math.min(1, oklchCoords(background)[0])), [1, ...lightnessValues], [...LIGHTNESS_BEZIER_LIGHT]);
        mapped.shift();
        for (const [index, value] of mapped.entries()) {
            const color = mixedScale[index];
            if (color !== undefined) {
                color.coords[0] = value;
            }
        }
        return mixedScale;
    }
    const bezierWeights = [...LIGHTNESS_BEZIER_DARK];
    const firstLightness = oklchCoords(firstMixed)[0];
    const backgroundRatio = Math.max(0, Math.min(1, oklchCoords(background)[0])) / firstLightness;
    if (backgroundRatio > 1) {
        for (let index = 0; index < bezierWeights.length; index++) {
            const factor = 3 * (backgroundRatio - 1);
            bezierWeights[index] =
                backgroundRatio > 1.5
                    ? 0
                    : Math.max(0, (bezierWeights[index] ?? 0) * (1 - factor));
        }
    }
    const lightnessValues = mixedScale.map((color) => oklchCoords(color)[0]);
    for (const [index, value] of mapLightness(oklchCoords(background)[0], lightnessValues, bezierWeights).entries()) {
        const color = mixedScale[index];
        if (color !== undefined) {
            color.coords[0] = value;
        }
    }
    return mixedScale;
}
/**
 * Generates a 12-step Radix-compatible palette from custom accent, gray, and background colors.
 */
export function generateRadixPalette({ appearance, accent, gray, background, }) {
    const backgroundColor = new Color(background).to('oklch');
    const grayScale = interpolateScale(new Color(gray).to('oklch'), appearance === 'light' ? lightGrayScales : darkGrayScales, backgroundColor);
    const accentColor = new Color(accent).to('oklch');
    let accentScale = interpolateScale(accentColor, appearance === 'light' ? lightAccentScales : darkAccentScales, backgroundColor);
    const backgroundHex = backgroundColor.to('srgb').toString({ format: 'hex' });
    const accentHex = accentColor.to('srgb').toString({ format: 'hex' });
    if (accentHex === '#000' || accentHex === '#fff') {
        accentScale = grayScale.map((color) => color.clone());
    }
    const step8 = 100 * accentColor.deltaEOK(requireColor(accentScale, 0)) < 25
        ? requireColor(accentScale, 8)
        : accentColor;
    const contrastColor = getContrastColor(step8);
    accentScale[8] = step8;
    accentScale[9] = tuneStep9(step8, [accentScale]);
    const maxChroma = Math.max(oklchCoords(requireColor(accentScale, 8))[1], oklchCoords(requireColor(accentScale, 7))[1]);
    const step10 = requireColor(accentScale, 10);
    step10.coords[1] = Math.min(maxChroma, oklchCoords(step10)[1]);
    const step11 = requireColor(accentScale, 11);
    step11.coords[1] = Math.min(maxChroma, oklchCoords(step11)[1]);
    const accentScaleHex = accentScale.map((color) => color.to('srgb').toString({ format: 'hex' }));
    const accentScaleAlpha = accentScaleHex.map((color) => toAlphaHex(color, backgroundHex));
    const accentContrast = contrastColor.to('srgb').toString({ format: 'hex' });
    const grayScaleHex = grayScale.map((color) => color.to('srgb').toString({ format: 'hex' }));
    const grayScaleAlpha = grayScaleHex.map((color) => toAlphaHex(color, backgroundHex));
    const accentSurfaceAlpha = appearance === 'light' ? 0.8 : 0.5;
    return {
        accentScale: accentScaleHex,
        accentScaleAlpha,
        accentContrast,
        grayScale: grayScaleHex,
        grayScaleAlpha,
        graySurface: appearance === 'light' ? '#ffffffcc' : 'rgba(0, 0, 0, 0.05)',
        accentSurface: toAlphaHex(accentScaleHex[1] ?? backgroundHex, backgroundHex, accentSurfaceAlpha),
        background: backgroundHex,
    };
}
//# sourceMappingURL=generateRadixPalette.js.map